<template>
    <div ref="rootRef"
         @click="handleVideoClick"
         class="relative w-full bg-zinc-950 flex-shrink-0 overflow-hidden touch-none"
         :class="{ 'z-50': shouldVideoBeLocked }"
         :style="{
         ...containerStyle,
         transform: 'translate3d(0, 0, 0)',
         willChange: 'transform'
     }">
        <!-- Video Background -->
        <div class="absolute inset-0">
            <!-- Locked State -->
            <div
                v-if="shouldVideoBeLocked"
                class="relative z-[1] mx-auto object-center h-full w-full"
                :class="[
                    mediaFitClass,
                ]"
            >
                <img
                    :src="lockPosterImageUrl"
                    :alt="videoData.title || 'Locked video'"
                    class="w-full h-full"
                    :class="[
                        mediaFitClass,
                    ]"
                />

                <div
                    v-if="showLockCard && !requiresAgeVerification"
                    @click="$emit('joinClick')"
                    class="absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 transform cursor-pointer w-[336px] rounded-2xl overflow-hidden backdrop-blur-xl border border-white/10 shadow-2xl"
                >
                    <!-- Header Section with Lock Icon -->
                    <div class="relative bg-gradient-to-b from-red-600/90 to-red-700/90 backdrop-blur-md px-3 py-4 flex flex-col items-center justify-center">
                        <div class="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center mb-4">
                            <font-awesome-icon
                                class="h-4 w-4 text-white"
                                :icon="['fa', 'lock']"
                            />
                        </div>

                        <p class="text-white text-center text-sm font-semibold tracking-wide uppercase opacity-90">
                            Free Limit Reached
                        </p>
                    </div>

                    <!-- Body Section -->
                    <div class="bg-black/80 backdrop-blur-md px-6 py-8 flex flex-col items-center">
                        <h3 class="text-white text-center text-xl font-bold mb-2">
                            Unlock Unlimited Access
                        </h3>

                        <p class="text-white/70 text-center text-sm mb-6 leading-relaxed">
                            Watch exclusive Naughty America<br />videos anytime, anywhere
                        </p>

                        <button
                            @click.stop="$emit('joinClick')"
                            class="w-full bg-red-600 hover:bg-red-700 active:bg-red-800 text-white px-6 py-3.5 rounded-xl font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
                        >
                            Unlock All Videos
                        </button>

                        <p class="text-white/40 text-xs text-center mt-4">
                            Join thousands of members today
                        </p>
                    </div>
                </div>
            </div>


            <img
                v-if="!shouldVideoBeLocked && posterImageUrl"
                :src="posterImageUrl"
                :alt="videoData.title || ''"
                :class="[
                    'absolute inset-0 z-[1] mx-auto h-full w-full object-center pointer-events-none',
                    mediaFitClass,
                ]"
                style="transform: translateZ(0); backface-visibility: hidden"
            />

            <!-- Video layer on top of poster: fades IN so we never reveal a black frame -->
            <div
                v-if="!shouldVideoBeLocked && renderVideo"
                :class="[
                    'absolute inset-0 z-[5] bg-zinc-950 transition-opacity duration-300 ease-out',
                    videoLayerVisible ? 'opacity-100' : 'opacity-0'
                ]"
                style="transform: translateZ(0); backface-visibility: hidden"
            >
                <video
                    ref="videoRef"
                    loop
                    playsinline
                    webkit-playsinline
                    disablePictureInPicture
                    crossorigin="anonymous"
                    controlsList="nodownload"
                    :preload="preloadMode || (shouldAutoplay ? 'auto' : 'metadata')"
                    disableremoteplayback
                    :autoplay="shouldAutoplay"
                    :muted="isMuted || shouldVideoBeLocked"
                    @waiting="isBuffering = shouldAutoplay"
                    @canplay="isBuffering = false"
                    @loadstart="isBuffering = shouldAutoplay"
                    @loadedmetadata="onVideoLoadedMetadata"
                    @play="onVideoPlay"
                    @pause="isPlaying = false"
                    :class="[
                        'relative mx-auto object-center h-full w-full',
                        mediaFitClass,
                    ]">

                    <source
                        :src="shouldVideoBeLocked ?
                            videoData.lock.videoUrl :
                            videoData.videoUrl_mp4_unbranded || videoData.videoUrl_mp4"
                        type="video/mp4"
                    />
                </video>
            </div>

            <!-- Buffering Spinner -->
            <div
                v-if="isBuffering"
                class="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
            >
                <div class="loading spinner">
                    <span class="text-white"></span>
                </div>
            </div>

            <!-- Play Indicator -->
            <div
                v-if="(canPlay || isPaused) && !isExpired && !shouldVideoBeLocked"
                class="absolute inset-0 flex items-center justify-center pointer-events-none z-10 text-white"
            >
                <div class="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                    <Play class="w-5 h-5 text-black ml-0.5" fill="black" />
                </div>
            </div>

        </div>

        <!-- In Production Badge -->
        <!--<div
            v-if="showInProductionBadge"
            class="absolute top-[72px] left-0 right-0 z-30 px-4"
        >
            <div class="relative rounded-xl bg-black/40 backdrop-blur-md border border-white/10 px-4 py-2.5">
                <button
                    @click="handleInProductionBadgeDismiss"
                    class="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition"
                    aria-label="Dismiss"
                >
                    <X class="w-3 h-3 text-white/60" />
                </button>

                <div class="flex items-center gap-2.5 pr-8">
                    <div class="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-400/25 flex items-center justify-center flex-shrink-0">
                        <Film class="w-4 h-4 text-amber-300" :stroke-width="2.5" />
                    </div>
                    <div class="min-w-0">
                        <p class="text-white text-[13px] font-semibold">
                            In Production
                        </p>
                        <p class="text-white/45 text-xs">
                            Scene being filmed
                        </p>
                    </div>
                </div>
            </div>
        </div>-->

        <!-- Released CTA -->
        <!--<div
            v-if="isReleased"
            class="absolute top-[72px] left-0 right-0 z-30 px-4"
        >
            <a
                @click.stop="handleWatchReleasedSceneClick"
                :href="route(routePrefix + '.scene.show', videoData.scene_slug)"
                target="_blank"
                class="block group w-full rounded-2xl bg-white/[0.08] hover:bg-white/[0.12] active:bg-white/[0.15] backdrop-blur-xl border border-white/10 hover:border-white/15 active:scale-[0.99] transition-all px-4 py-3.5 shadow-lg"
            >
                <div class="flex items-center justify-between gap-3">
                    <div class="flex items-center gap-3 min-w-0">
                        <div class="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-400/20 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/20 transition-colors">
                            <Play class="w-4 h-4 text-emerald-400 ml-0.5" fill="currentColor" :stroke-width="0" />
                        </div>

                        <div class="text-left min-w-0">
                            <p class="text-white text-[15px] font-semibold leading-tight tracking-tight">
                                Watch Released Scene
                            </p>
                            <p v-if="videoData.released_at" class="text-white/50 text-[12px] mt-0.5 font-medium tracking-wide">
                                Released {{ formatReleaseDate(videoData.released_at) }}
                            </p>
                        </div>
                    </div>

                    <div class="shrink-0">
                        <svg
                            class="w-5 h-5 text-white/40 group-hover:text-white/60 group-hover:translate-x-0.5 transition-all"
                            fill="none"
                            stroke="currentColor"
                            :stroke-width="2"
                            viewBox="0 0 24 24"
                        >
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </div>
                </div>
            </a>
        </div>-->

    </div>
</template>

<script setup>
import {ref, computed, onMounted, watch, onUnmounted, inject} from 'vue'
import { X, Film, Play } from 'lucide-vue-next'
import VideoControls from './VideoControls.vue'
import VideoOverlay from './VideoOverlay.vue'
import { trackAuditionAnalyticsEvent } from "@/Helpers/auditions";
import {useMutedStore} from "@/store/useMutedStore";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

const props = defineProps({
    videoData: {
        type: Object,
        default: () => ({})
    },
    isUserLoggedIn: {
        type: Boolean,
        default: false
    },
    routePrefix: {
        type: String
    },
    shouldAutoplay: {
        type: Boolean,
        default: false
    },
    shouldVideoBeLocked: {
        type: Boolean,
        default: false
    },
    transitionName: {
        type: String,
        default: 'fade'
    },
    requiresAgeVerification: {
        type: Boolean,
        default: false
    },
    isExpired: {
        type: Boolean,
        default: false
    },
    // '' keeps the historical preload behavior (auto for the autoplaying slide, metadata otherwise).
    preloadMode: {
        type: String,
        default: ''
    },
    // For feeds that keep slides mounted across swipes instead of remounting them.
    resetPlaybackOnAutoplayChange: {
        type: Boolean,
        default: false
    },
    renderVideo: {
        type: Boolean,
        default: true
    },
    respectIntrinsicAspect: {
        type: Boolean,
        default: false
    },
    showLockCard: {
        type: Boolean,
        default: true
    }
})

const emit = defineEmits([
    'videoClick',
])

const hasInteracted = inject('hasInteracted')
const mutedStore = useMutedStore()

const IN_PRODUCTION_BADGE_DISMISSED_KEY = 'audition_in_production_badge_dismissed'

const rootRef = ref(null)
const videoRef = ref(null)
const isPaused = ref(false)
const videoProgress = ref(0)
const isMuted = ref(true)
const isPlaying = ref(false)
const isBuffering = ref(false)
const isMobileDevice = ref(false)
const viewEventTracked = ref(false)
const isMobile = /Mobi|Android|CriOS|iOS/i.test(navigator.userAgent)

let progressInterval = null

const isReleased = computed(() => props.videoData.is_audition && props.videoData.release_status === 2 && props.videoData.scene_slug)
const inProduction = computed(() => props.videoData.is_audition && props.videoData.release_status === 1)
const showInProductionBadge = computed(() => props.videoData.is_audition && inProduction.value && !isInProductionBadgeDismissed.value)
const canPlay = computed(() => !isPlaying && !isBuffering && !props.shouldVideoBeLocked)
const windowHeight = ref(window.innerHeight)
const windowWidth = ref(window.innerWidth)

// Landscape key first: the clips feed emits exactly one poster key, matching the clip's
// orientation. Reels payloads only ever carry the portrait key, so they degrade to the
// old expression.
const posterImageUrl = computed(() =>
    props.videoData?.start_card_video_images?.image_1066x600_webp
    ?? props.videoData?.start_card_video_images?.image_600x1066_webp
    ?? null
)

const lockPosterImageUrl = computed(() =>
    props.videoData?.lock?.start_card_video_images?.image_1066x600_webp
    ?? props.videoData?.lock?.start_card_video_images?.image_600x1066_webp
    ?? null
)

// Runtime reading, for clips the API reported no dimensions for. No reset watcher: the feed
// that opts in keys its slides by clip id, so a mounted instance keeps one videoData for life.
const videoElementHasRealDimensions = ref(false)

const isRealDimensionPair = (width, height) =>
    Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0

const hasApiDimensions = computed(() => isRealDimensionPair(
    Number(props.videoData?.width),
    Number(props.videoData?.height)
))

// `is_vertical` is now derived server-side from the clip's stored dimensions, so trusting it
// is safe — it is only the fallback for a clip the API reported no dimensions for. Resolution
// order: API dimensions (authoritative, known before any media loads), then the video element's
// own. The poster is still not an aspect source — its aspect now merely matches the API
// dimensions we already prefer, so the poster -> video crossfade no longer jumps for landscape
// clips. Any real aspect means contain; only the heuristic can still crop.
const hasIntrinsicDimensions = computed(() => props.respectIntrinsicAspect && (
    hasApiDimensions.value
    || videoElementHasRealDimensions.value
))

// Known dimensions means never crop: the zinc-950 container supplies the bars. One computed
// for both layers so the poster -> video crossfade can never shift the geometry.
const mediaFitClass = computed(() => {
    if (hasIntrinsicDimensions.value) {
        return 'object-contain'
    }

    return props.videoData.is_vertical ? 'object-cover' : 'object-contain'
})

const onVideoLoadedMetadata = () => {
    if (!videoRef.value) {
        return
    }

    videoElementHasRealDimensions.value = isRealDimensionPair(
        videoRef.value.videoWidth,
        videoRef.value.videoHeight
    )
}

const hasStartedPlaying = ref(false)
// When no poster, show video layer immediately; otherwise fade it in after first frame
const videoLayerVisible = computed(() =>
    !posterImageUrl.value || hasStartedPlaying.value
)

const containerStyle = computed(() => {
    // Desktop: fixed dimensions
    if (windowWidth.value >= 1024) {
        return {
            width: '560px',
            height: `${windowHeight.value}px`,
        }
    }

    // Mobile: use visual viewport height
    return {
        height: `${windowHeight.value}px`,
        width: '100%'
    }
})

const formatReleaseDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
    })
}

// Watch shouldAutoplay prop to control video
watch(() => props.shouldAutoplay, (newVal) => {
    if (!videoRef.value) {
        return
    }

    if (newVal) {
        if (props.resetPlaybackOnAutoplayChange) {
            // The slide was not remounted, so re-apply the mute state onMounted would have.
            isMuted.value = mutedStore.initialize(hasInteracted.value)
            videoRef.value.muted = isMuted.value
        }

        playVideo()

        return
    }

    pauseVideo()

    // A slide that stops being current must never carry a latched spinner forward.
    isBuffering.value = false

    if (props.resetPlaybackOnAutoplayChange) {
        // Restart-from-zero + poster-again semantics a remount used to provide; seeking to 0
        // does not discard the buffered ranges.
        videoRef.value.currentTime = 0
        videoProgress.value = 0
        hasStartedPlaying.value = false
        isPaused.value = false
    }
})

// Track video progress
const startProgressTracking = () => {
    stopProgressTracking()

    progressInterval = setInterval(() => {
        if (videoRef.value && videoRef.value.duration) {
            videoProgress.value = (videoRef.value.currentTime / videoRef.value.duration) * 100
        }
    }, 100)
}

const stopProgressTracking = () => {
    if (progressInterval) {
        clearInterval(progressInterval)
        progressInterval = null
    }
}

const onVideoPlay = () => {
    isPlaying.value = true
    isPaused.value = false
    isBuffering.value = false
    startProgressTracking()

    if ( ! viewEventTracked.value) {
        trackAuditionAnalyticsEvent('Audition Viewed', props.videoData)
        viewEventTracked.value = true
    }

    // Delay poster fade until first video frame has painted to reduce flicker
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            hasStartedPlaying.value = true
        })
    })
}

const playVideo = async () => {
    if (!videoRef.value) return

    try {
        await videoRef.value.play()
        // onVideoPlay runs via @play handler
    } catch (error) {
        console.error('Play failed:', error)
        // If play fails, try muted
        if (videoRef.value && !videoRef.value.muted) {
            isMuted.value = true
            videoRef.value.muted = true
            mutedStore.setMuted(true)
            try {
                await videoRef.value.play()
                // onVideoPlay runs via @play handler
            } catch (err) {
                console.error('Muted play also failed:', err)
            }
        }
    }
}

const pauseVideo = () => {
    if (!videoRef.value) return

    videoRef.value.pause()
    isPlaying.value = false
    isPaused.value = true
    stopProgressTracking()
}

// Handle video click for play/pause
const handleVideoClick = (e) => {
    // Don't toggle if clicking on buttons/controls
    if (e.target.tagName === 'BUTTON' || e.target.closest('button') || e.target.closest('a')) {
        return
    }

    if (props.isExpired) return

    if (!props.shouldVideoBeLocked && videoRef.value) {
        if (videoRef.value.paused) {
            playVideo()
        } else {
            pauseVideo()
        }
    }

    emit('videoClick')
}

// Handle mute toggle
const handleToggleMute = () => {
    if (videoRef.value) {
        isMuted.value = !isMuted.value
        videoRef.value.muted = isMuted.value
        mutedStore.setMuted(isMuted.value)
    }
}

// Handle seeking
const handleSeek = (percentage) => {
    if (videoRef.value && videoRef.value.duration) {
        const wasPlaying = !videoRef.value.paused // Track playback state before seeking
        const newTime = (percentage / 100) * videoRef.value.duration

        videoRef.value.currentTime = newTime
        videoProgress.value = percentage

        // Force resume if it was playing
        if (wasPlaying) {
            // Use nextTick or setTimeout to ensure the seek completes first
            setTimeout(() => {
                if (videoRef.value && videoRef.value.paused) {
                    playVideo()
                }
            }, 50)
        }
    }
}

// Update dimensions on resize/orientation change so containerStyle stays in sync
const updateWindowSize = () => {
    windowWidth.value = window.innerWidth
    // Use visualViewport if available (more accurate for mobile browsers)
    windowHeight.value = window.visualViewport?.height ?? window.innerHeight
}

// In production status badge handling
const wasInProductionBadgeDismissed = () => {
    try {
        const dismissedVideos = localStorage.getItem(IN_PRODUCTION_BADGE_DISMISSED_KEY)
        if (dismissedVideos) {
            const parsed = JSON.parse(dismissedVideos)
            // Check if this specific video ID is in the dismissed list
            return parsed.includes(props.videoData.id)
        }
    } catch (e) {
        console.error('Error reading dismissed status badges:', e)
    }
    return false
}

const saveInProductionBadgeDismissed = () => {
    try {
        const dismissedVideos = localStorage.getItem(IN_PRODUCTION_BADGE_DISMISSED_KEY)
        let parsed = dismissedVideos ? JSON.parse(dismissedVideos) : []

        // Add current video ID if not already in the list
        if (!parsed.includes(props.videoData.id)) {
            parsed.push(props.videoData.id)
            localStorage.setItem(IN_PRODUCTION_BADGE_DISMISSED_KEY, JSON.stringify(parsed))
        }
    } catch (e) {
        console.error('Error saving dismissed status badge:', e)
    }
}

const handleInProductionBadgeDismiss = () => {
    isInProductionBadgeDismissed.value = true
    saveInProductionBadgeDismissed()
}

const isInProductionBadgeDismissed = ref(wasInProductionBadgeDismissed()) // init from storage

onMounted(() => {
    window.addEventListener('resize', updateWindowSize)
    window.addEventListener('orientationchange', updateWindowSize)

    // Visual viewport is more accurate for mobile browsers with dynamic toolbars
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', updateWindowSize)
    }

    updateWindowSize()

    isMobileDevice.value = isMobile

    // Initialize mute state from store
    isMuted.value = mutedStore.initialize(hasInteracted.value)

    if (videoRef.value) {
        videoRef.value.muted = isMuted.value

        // Start playback if needed (@play in template handles onVideoPlay)
        if (props.shouldAutoplay && !props.shouldVideoBeLocked) {
            setTimeout(() => {
                playVideo()
            }, 150)
        }
    }
})

/** Unload video from memory: pause, clear src, release buffer. Critical on mobile after many swipes. */
function unloadVideoElement() {
    if (!videoRef.value) return
    const video = videoRef.value
    video.pause()
    video.removeAttribute('src')
    const source = video.querySelector('source')
    if (source) {
        source.removeAttribute('src')
    }
    video.load()
}

onUnmounted(() => {
    stopProgressTracking()

    window.removeEventListener('resize', updateWindowSize)
    window.removeEventListener('orientationchange', updateWindowSize)
    if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateWindowSize)
    }

    unloadVideoElement()
})

const handleWatchReleasedSceneClick = () => {
    trackAuditionAnalyticsEvent('Watch Scene Button Clicked', props.videoData);
    window.open(route(props.routePrefix + '.scene.show', props.videoData.scene_slug), '_blank')
}

// Expose values needed by parent for controls
defineExpose({
    videoProgress,
    isMuted,
    handleToggleMute,
    handleSeek,
    pause: pauseVideo,
    play: playVideo,
})
</script>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
